import React from "react";
import DOMPurify from "isomorphic-dompurify";
import styles from "./Copyright.module.scss";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { CustomLink } from "../CustomLink";
import { ImageType, LinkType } from "@/types/baseTypes";

export const genCopyright = (
  key: number,
  text: string,
  url: string,
  ariaLabel?: string,
  className?: string,
) => {
  if (!text && !url) {
    return;
  }

  if (text) {
    if (!url) {
      return (
        <span
          className={`${className || ""} copyright`}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
          key={key}
        />
      );
    }

    return (
      <CustomLink
        href={url}
        className={`${className ?? ""} copyright`}
        key={key}
        target="_blank"
        aria-label={ariaLabel}
      >
        <span
          className="copyright"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
        />
      </CustomLink>
    );
  }
};

interface ICopyright {
  imgfhd: ImageType | false;
  imgdsk: ImageType | false;
  imgtab: ImageType | false;
  imgmob: ImageType | false;
  copyrightPosition?: "top" | "bottom";
  showCopyright?: boolean;
}

export const generateCopyrightJSX = (
  image: ImageType | undefined,
  getLabel: (key: string, fallback?: string) => string,
) => {
  const has2Copyrights = image?.acf?.text_1 && image?.acf?.text_2;
  const hasCopyright = image?.acf?.text_1 || image?.acf?.text_2;
  const ariaLabel1 = image?.acf?.text_1
    ? getLabel("aria_label_copyright_link_1").replace(
        `$text`,
        image?.acf?.text_1,
      )
    : "";
  const ariaLabel2 = image?.acf?.text_1
    ? getLabel("aria_label_copyright_link_2").replace(
        `$text`,
        image?.acf?.text_2,
      )
    : "";

  const copyrightJSX = hasCopyright
    ? [
        genCopyright(0, image?.acf?.text_1, image?.acf?.link_1, ariaLabel1),
        ...(has2Copyrights
          ? [
              <span style={{ padding: "0 0.25rem", color: "#fff" }} key={2}>
                |
              </span>,
            ]
          : []),
        genCopyright(1, image?.acf?.text_2, image?.acf?.link_2, ariaLabel2),
      ]
    : [];

  return copyrightJSX;
};

const CopyrightFromImage = ({
  imgfhd,
  imgdsk,
  imgtab,
  imgmob,
  showCopyright = true,
  copyrightPosition = "bottom",
}: ICopyright) => {
  const { getLabel } = useGlobalContext();
  const image = imgfhd || imgdsk || imgtab || imgmob || undefined;
  const copyrightJSX = generateCopyrightJSX(image, getLabel);

  return copyrightJSX.length && showCopyright ? (
    <span
      data-position={"has-text-right"}
      className={`${styles[`image-copyright`]} copyright ${copyrightPosition === "top" ? styles.top : ""}`}
    >
      {copyrightJSX}
    </span>
  ) : (
    <></>
  );
};

export default CopyrightFromImage;

export interface CopyRightType {
  caption_position: "has-text-left" | "has-text-centered" | "has-text-right";
  caption: string;
  link: LinkType | false;
}

export const CopyRightText = ({
  caption = "",
  caption_position = "has-text-right",
  link,
}: CopyRightType) => {
  return caption.length ? (
    <span
      data-position={caption_position}
      className={styles[`image-copyright`]}
    >
      {genCopyright(0, caption, link ? link.url : "")}
    </span>
  ) : (
    <></>
  );
};
